<script lang="ts">
  import * as v from "valibot";
  import AuthLayout from "../auth/AuthLayout.svelte";
  import Field from "$lib/components/Field.svelte";
  import Button from "$lib/components/Button.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Badge from "$lib/components/Badge.svelte";
  import { startEmailVerification } from "$lib/api/endpoints";
  import { session } from "$lib/stores/session.svelte";
  import { router } from "../../router.svelte";
  import { createForm } from "$lib/forms/form.svelte";
  import { track } from "$lib/telemetry/emit";
  import { SUPPORT_EMAIL } from "$lib/domain/access";

  // Read the invite token into memory, then immediately scrub it from history. It is
  // forwarded once, inside the email/start request — never rendered or logged.
  let inviteToken = $state<string | null>(router.match.query.get("token"));
  router.scrubQuery(["token"]);

  let sentTo = $state<string | null>(null);

  const schema = v.object({
    email: v.pipe(v.string(), v.trim(), v.email("Enter a valid email."))
  });

  const form = createForm(schema, { email: "" }, async (values) => {
    if (!inviteToken) return;
    await startEmailVerification({
      email: values.email.trim(),
      inviteToken,
      returnTo: "/"
    });
    track({ name: "auth.email.requested", context: "invite" });
    sentTo = values.email.trim();
  });

  // The invitation-bound start call is the first place the inviting organization's
  // network policy is enforced, so a denial here is a network problem, not a bad link.
  const networkDenied = $derived(form.formError?.code === "organization_ip_denied");

  function goToPortal(): void {
    router.navigate("/", { replace: true });
  }

  async function signOutAndRestart(): Promise<void> {
    await session.logout();
  }
</script>

{#snippet seatNotice()}
  <Banner tone="info" title="Membership, not a seat">
    Accepting this invitation makes you a member of the organization. It does
    <strong>not</strong> reserve or guarantee an EpiDev product seat — an owner or admin
    assigns seats separately, and you may not have one straight away.
  </Banner>
{/snippet}

{#snippet errorBanner()}
  {#if form.formError}
    {#if networkDenied}
      <Banner tone="warn" title="This network isn't allowed" correlationId={form.formError.correlationId}>
        The organization that invited you only accepts connections from approved networks, and
        this one isn't approved. Reconnect from your office network or company VPN and open the
        invitation link again. If nobody can get in, email
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </Banner>
    {:else}
      <Banner tone="error" title="We couldn't send the email" correlationId={form.formError.correlationId}>
        {form.formError.userMessage}
      </Banner>
    {/if}
  {/if}
{/snippet}

<AuthLayout
  title={sentTo ? "Check your email" : "Accept your invitation"}
  subtitle={sentTo ? undefined : "Join your organization on EpiDev."}
>
  {#if !inviteToken}
    <Banner tone="error" title="Invalid invitation">
      This invitation link is missing, has already been used, or was replaced by a newer one.
      Ask an owner or admin of the organization to resend your invitation — resending always
      invalidates the previous link.
    </Banner>
    <Button href="/login" fullWidth>Back to sign in</Button>
  {:else if sentTo}
    <div class="stack" role="status" aria-live="polite">
      <p class="sent-copy">
        We sent a secure verification link to <span class="mono">{sentTo}</span>. Open it
        to finish joining your organization.
      </p>
      <p class="note">
        If you're new to EpiDev, your account is created automatically after you verify
        your email — there are no passwords.
      </p>
      <p class="note">
        <strong>If the invitation had already expired or been resent</strong>, that link signs
        you in without joining the organization. Ask for a fresh invitation and use the newest
        email you receive.
      </p>
      <p class="note">
        Joining does not assign you a product seat. If you land on a "no seat yet" screen, ask
        an organization administrator to assign one.
      </p>
    </div>
  {:else if session.authenticated}
    <Banner tone="info" title={`Signed in as ${session.user?.displayName ?? ""}`}>
      Invitations are completed through email verification. If this invite is for the
      account you're signed in with, continue below. If it's for a different email
      address, sign out first.
    </Banner>
    {@render seatNotice()}
    {@render errorBanner()}
    <form onsubmit={(e) => { e.preventDefault(); void form.submit(); }} class="stack">
      <Field
        id="inv-email"
        label="Work email"
        type="email"
        bind:value={form.values.email}
        autocomplete="email"
        error={form.touched.email ? form.errors.email : undefined}
        onblur={() => form.touch("email")}
        required
      />
      <Button type="submit" fullWidth loading={form.busy}>Email me a verification link</Button>
    </form>
    <div class="row">
      <Button variant="ghost" onclick={signOutAndRestart}>Sign out</Button>
      <Button variant="ghost" onclick={goToPortal}>Go to portal</Button>
    </div>
  {:else}
    {@render seatNotice()}
    {@render errorBanner()}
    <form onsubmit={(e) => { e.preventDefault(); void form.submit(); }} class="stack">
      <Field
        id="inv-email"
        label="Work email"
        type="email"
        bind:value={form.values.email}
        autocomplete="email"
        error={form.touched.email ? form.errors.email : undefined}
        onblur={() => form.touch("email")}
        required
      />
      <Button type="submit" fullWidth loading={form.busy}>Accept invitation</Button>
      <p class="note">
        <Badge tone="muted">No seat used</Badge>
        We'll email you a secure link to verify this address. Your account is created
        automatically after verification — no password to set.
      </p>
    </form>
  {/if}
</AuthLayout>

<style>
  .row {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }
  .sent-copy {
    font-size: 14.5px;
    margin: 0;
  }
  .note {
    font-size: 13px;
    color: var(--muted);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
</style>
